import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ReplyModal.module.css';

/**
 * ReplyModal — modale de réponse à un avis avec génération IA + publication.
 *
 * Props :
 *  - review : l'avis à répondre (id, author, text, rating, date)
 *  - gmbConnected : bool — si false, bouton "Copier la réponse" au lieu de "Publier"
 *  - onClose : fermer la modale
 *  - onGenerate(review) → returns Promise<string> — texte généré par IA
 *  - onPublish(review, text) → returns Promise<bool> — publication sur Google
 */
export default function ReplyModal({
  review,
  gmbConnected = false,
  onClose,
  onGenerate,
  onPublish,
}) {
  const { t } = useTranslation('tabs');
  const [text, setText] = useState(review?.replyText || '');
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset le texte quand l'avis change
  useEffect(() => {
    setText(review?.replyText || '');
    setCopied(false);
  }, [review?.id]);

  // Fermer avec Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleGenerate = useCallback(async () => {
    if (!onGenerate) return;
    setGenerating(true);
    try {
      const generated = await onGenerate(review);
      if (generated) setText(generated);
    } finally {
      setGenerating(false);
    }
  }, [onGenerate, review]);

  const handlePublish = useCallback(async () => {
    if (!onPublish || !text.trim()) return;
    setPublishing(true);
    try {
      const ok = await onPublish(review, text);
      if (ok) onClose?.();
    } finally {
      setPublishing(false);
    }
  }, [onPublish, review, text, onClose]);

  const handleCopy = useCallback(async () => {
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('Impossible de copier — copiez manuellement le texte.');
    }
  }, [text]);

  if (!review) return null;

  const stars = '★'.repeat(Math.round(review.rating || 0)) + '☆'.repeat(5 - Math.round(review.rating || 0));
  const charCount = text.length;
  const charLimit = 4096; // limite Google Business Profile

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <header className={styles.header}>
          <h2>{t('repondre.modal.title')}</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Fermer">×</button>
        </header>

        {/* Avis original */}
        <section className={styles.review}>
          <div className={styles.reviewHead}>
            <div className={styles.author}>
              <div className={styles.avatar}>
                {(review.author || '?').charAt(0).toUpperCase()}
              </div>
              <div>
                <div className={styles.authorName}>{review.author || 'Client anonyme'}</div>
                <div className={styles.stars}>{stars}</div>
              </div>
            </div>
          </div>
          <p className={styles.reviewText}>{review.text || <em>Aucun texte</em>}</p>
        </section>

        {/* Bandeau GMB */}
        {!gmbConnected && (
          <div className={styles.gmbWarning}>
            {t('repondre.modal.gmb_disconnected')}
          </div>
        )}

        {/* Textarea + génération */}
        <section className={styles.replyArea}>
          <div className={styles.replyHeader}>
            <label>{t('repondre.modal.label_response')}</label>
            <button
              type="button"
              className={styles.btnGenerate}
              onClick={handleGenerate}
              disabled={generating || !review.text || review.text.trim().length === 0}
              title={!review.text ? t('repondre.modal.generate_disabled_tip') : ''}
            >
              {generating ? t('repondre.modal.generating') : t('repondre.modal.generate')}
            </button>
          </div>
          {(!review.text || review.text.trim().length === 0) && (
            <div className={styles.noTextWarning}>
              {t('repondre.modal.no_text_hint', { rating: review.rating })}
            </div>
          )}
          <textarea
            className={styles.textarea}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('repondre.modal.placeholder')}
            rows={6}
          />
          <div className={styles.charCount}>
            <span className={charCount > charLimit ? styles.charOverflow : ''}>
              {t('repondre.modal.char_count', { count: charCount, limit: charLimit })}
            </span>
          </div>
        </section>

        {/* Actions */}
        <footer className={styles.actions}>
          <button type="button" className={styles.btnGhost} onClick={onClose}>
            {t('common.cancel')}
          </button>
          {gmbConnected ? (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handlePublish}
              disabled={!text.trim() || publishing || charCount > charLimit}
            >
              {publishing ? t('repondre.btn_publishing') : t('repondre.btn_publish')}
            </button>
          ) : (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleCopy}
              disabled={!text.trim()}
            >
              {copied ? t('repondre.modal.copied') : t('repondre.modal.copy_response')}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
